import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { 
  Customer, 
  CustomerFormData, 
  CustomerValidationError, 
  validateCustomerForm, 
  formatCustomerData,
  makeApiCall,
  makeJsonApiCall,
  getAuthHeaders
} from '../utils/customerUtils'
import { PartnerSearchDropdown } from './PartnerSearchDropdown'
import { Partner } from './PartnerForm'

interface AssociatedCustomer {
  id?: number
  first_name: string
  last_name: string
  email: string
  phone: string
  language: string
  referred_by?: string
  partner_id?: number | null
}

interface CustomerFormProps {
  customer?: Customer
  onSubmit?: (customer: Customer) => void
  onCancel?: () => void
  isModal?: boolean
}

export function CustomerForm({ customer, onSubmit, onCancel, isModal = false }: CustomerFormProps) {
  const { t } = useTranslation()
  
  const [formData, setFormData] = useState<CustomerFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    language: 'fr',
    custom_link: '',
    referred_by: ''
  })
  
  const [selectedPartner, setSelectedPartner] = useState<number | null>(null)
  const [associatedCustomers, setAssociatedCustomers] = useState<AssociatedCustomer[]>([])
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  
  const [errors, setErrors] = useState<CustomerValidationError[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const isEditing = !!customer

  useEffect(() => {
    if (customer) {
      setFormData({
        first_name: customer.first_name || '',
        last_name: customer.last_name || '',
        name: customer.name,  // Keep legacy name for display/fallback
        email: customer.email,
        phone: customer.phone || '',
        language: customer.language || 'fr',
        custom_link: customer.custom_link || '',
        referred_by: customer.referred_by || ''
      })
      
      // Set partner if exists
      setSelectedPartner((customer as any).partner_id || null)
      
      // Fetch associated customers for this customer
      fetchAssociatedCustomers(customer.id)
    }
  }, [customer])

  const fetchAssociatedCustomers = async (customerId: number) => {
    if (!customerId) return

    try {
      const response = await makeApiCall(`api/customer-associations/${customerId}`)

      if (response.ok) {
        const data = await response.json()
        const customers = data.associations?.map((assoc: any) => ({
          id: assoc.id,
          first_name: assoc.first_name || '',
          last_name: assoc.last_name || '',
          email: assoc.email,
          phone: assoc.phone,
          language: assoc.language || 'fr',
          referred_by: assoc.referred_by || ''
        })) || []
        setAssociatedCustomers(customers)
      }
    } catch (err) {
      console.error('Error fetching associated customers:', err)
    }
  }

  useEffect(() => {
    // Clear success message after 3 seconds
    if (submitSuccess) {
      const timer = setTimeout(() => setSubmitSuccess(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [submitSuccess])

  const handleInputChange = (field: keyof CustomerFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear field-specific errors
    setErrors(prev => prev.filter(error => error.field !== field))
    setSubmitError(null)
  }

  const getFieldError = (field: string): string | undefined => {
    return errors.find(error => error.field === field)?.message
  }

  const handleAddAssociatedCustomer = () => {
    setAssociatedCustomers(prev => [...prev, {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      language: 'fr',
      referred_by: formData.referred_by || '',
      partner_id: selectedPartner  // Auto-populate from main customer
    }])
  }

  const handleAssociatedCustomerChange = (index: number, field: keyof AssociatedCustomer, value: string | number | null) => {
    setAssociatedCustomers(prev => prev.map((customer, i) => 
      i === index ? { ...customer, [field]: value } : customer
    ))
  }

  const handleRemoveAssociatedCustomer = async (index: number) => {
    const customerToRemove = associatedCustomers[index]
    
    if (customer?.id && customerToRemove.id && customerToRemove.id > 1000000) {
      // If editing existing customer with real ID, remove association via API
      try {
        const response = await makeApiCall('api/customer-associations', {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            customer_id: customer.id,
            associated_customer_id: customerToRemove.id,
            association_type: 'created_together'
          })
        })

        if (response.ok) {
          setAssociatedCustomers(prev => prev.filter((_, i) => i !== index))
        }
      } catch (err) {
        console.error('Error removing associated customer:', err)
      }
    } else {
      // If creating new customer or temp customer, remove from local state
      setAssociatedCustomers(prev => prev.filter((_, i) => i !== index))
    }
  }

  // Sanitize phone number - remove all non-numeric characters
  const sanitizePhone = (phone: string): string => {
    return phone.replace(/\D/g, '')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate form
    const formattedData = formatCustomerData(formData)
    
    // Sanitize phone numbers - strip all non-numeric characters
    if (formattedData.phone) {
      formattedData.phone = sanitizePhone(formattedData.phone)
    }
    
    const validationErrors = validateCustomerForm(formattedData)
    
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    setIsSubmitting(true)
    setSubmitError(null)
    setErrors([])

    try {
      const url = isEditing ? `api/customers/${customer.id}` : 'api/customers'
      const method = isEditing ? 'PUT' : 'POST'

      const submitData = {
        ...formattedData,
        partner_id: selectedPartner || null
      }

      const response = await makeApiCall(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(submitData)
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 409) {
          setErrors([{ field: 'email', message: 'A customer with this email already exists' }])
          return
        }
        throw new Error(data.message || `Failed to ${isEditing ? 'update' : 'create'} customer`)
      }

      setSubmitSuccess(true)

      // Create associated customers if this was a new customer and there are customers to add
      if (!isEditing && associatedCustomers.length > 0) {
        for (const associatedCustomer of associatedCustomers) {
          // Skip if fields are empty
          if (!associatedCustomer.first_name.trim() || !associatedCustomer.last_name.trim() || !associatedCustomer.email.trim()) {
            continue
          }

          try {
            // Create the new customer with sanitized phone
            const createResponse = await makeJsonApiCall('api/customers', 'POST', {
              first_name: associatedCustomer.first_name,
              last_name: associatedCustomer.last_name,
              email: associatedCustomer.email,
              phone: associatedCustomer.phone ? sanitizePhone(associatedCustomer.phone) : '',
              language: associatedCustomer.language,
              referred_by: associatedCustomer.referred_by || '',
              partner_id: associatedCustomer.partner_id || null
            })

            if (createResponse.ok) {
              const createData = await createResponse.json()
              const newCustomerId = createData.customer.id

              // Create the association
              await makeJsonApiCall('api/customer-associations', 'POST', {
                customer_id: data.customer.id,
                associated_customer_id: newCustomerId,
                association_type: 'created_together'
              })
            }
          } catch (err) {
            console.error('Error creating associated customer:', err)
          }
        }
      }
      
      if (onSubmit) {
        onSubmit(data.customer)
      }

    } catch (error) {
      console.error('Error submitting customer:', error)
      setSubmitError(error instanceof Error ? error.message : 'An unexpected error occurred')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={`max-w-4xl mx-auto ${isModal ? 'w-full' : ''}`}>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Primary Customer Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
            {t('customers.form.primaryCustomerInformation')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.form.firstName')} <span className="text-red-500">{t('customers.form.required')}</span>
              </label>
              <input
                type="text"
                id="first_name"
                value={formData.first_name || ''}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  getFieldError('first_name') ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('customers.form.enterFirstName')}
                required
              />
              {getFieldError('first_name') && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{getFieldError('first_name')}</p>
              )}
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.form.lastName')} <span className="text-red-500">{t('customers.form.required')}</span>
              </label>
              <input
                type="text"
                id="last_name"
                value={formData.last_name || ''}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  getFieldError('last_name') ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('customers.form.enterLastName')}
                required
              />
              {getFieldError('last_name') && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{getFieldError('last_name')}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.form.emailAddress')} <span className="text-red-500">{t('customers.form.required')}</span>
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  getFieldError('email') ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('customers.form.enterEmail')}
                required
              />
              {getFieldError('email') && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{getFieldError('email')}</p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.form.phoneNumber')}
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
                placeholder={t('customers.form.enterPhone')}
              />
            </div>

            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.form.preferredLanguage')}
              </label>
              <select
                id="language"
                value={formData.language}
                onChange={(e) => handleInputChange('language', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:text-white"
              >
                <option value="fr">{t('customers.form.languageFrench')}</option>
                <option value="en">{t('customers.form.languageEnglish')}</option>
              </select>
            </div>

            <div>
              <label htmlFor="custom_link" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.form.customLink')}
              </label>
              <input
                type="url"
                id="custom_link"
                value={formData.custom_link || ''}
                onChange={(e) => handleInputChange('custom_link', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                  getFieldError('custom_link') ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={t('customers.form.enterCustomLink')}
              />
              {getFieldError('custom_link') && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{getFieldError('custom_link')}</p>
              )}
            </div>

            <div>
              <label htmlFor="partner" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.form.referredBy')} <span className="text-gray-500 text-xs">{t('partners.form.optional')}</span>
              </label>
              <PartnerSearchDropdown
                selectedPartnerId={selectedPartner}
                onSelect={(partner) => setSelectedPartner(partner?.id || null)}
                placeholder={t('customers.form.enterReferredBy')}
              />
            </div>
          </div>
        </div>

        {/* Associated Customers Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              {t('customers.form.associatedCustomers')}
            </h3>
            <button
              type="button"
              onClick={handleAddAssociatedCustomer}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
            >
              {t('customers.form.addCustomer')}
            </button>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('customers.form.associatedCustomersDescription')}
          </p>

          {/* Associated Customers List/Forms */}
          {associatedCustomers.length > 0 && (
            <div className="space-y-4">
              {associatedCustomers.map((associatedCustomer, index) => (
                <div key={index} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.form.firstName')} <span className="text-red-500">{t('customers.form.required')}</span>
                      </label>
                      <input
                        type="text"
                        value={associatedCustomer.first_name}
                        onChange={(e) => handleAssociatedCustomerChange(index, 'first_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                        placeholder={t('customers.form.enterFirstName')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.form.lastName')} <span className="text-red-500">{t('customers.form.required')}</span>
                      </label>
                      <input
                        type="text"
                        value={associatedCustomer.last_name}
                        onChange={(e) => handleAssociatedCustomerChange(index, 'last_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                        placeholder={t('customers.form.enterLastName')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.form.emailAddress')} <span className="text-red-500">{t('customers.form.required')}</span>
                      </label>
                      <input
                        type="email"
                        value={associatedCustomer.email}
                        onChange={(e) => handleAssociatedCustomerChange(index, 'email', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                        placeholder={t('customers.form.enterEmail')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.form.phoneNumber')}
                      </label>
                      <input
                        type="tel"
                        value={associatedCustomer.phone}
                        onChange={(e) => handleAssociatedCustomerChange(index, 'phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                        placeholder={t('customers.form.enterPhone')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.form.preferredLanguage')}
                      </label>
                      <select
                        value={associatedCustomer.language}
                        onChange={(e) => handleAssociatedCustomerChange(index, 'language', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900"
                      >
                        <option value="fr">{t('customers.form.languageFrench')}</option>
                        <option value="en">{t('customers.form.languageEnglish')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('customers.form.referredBy')}
                      </label>
                      <PartnerSearchDropdown
                        selectedPartnerId={associatedCustomer.partner_id}
                        onSelect={(partner) => handleAssociatedCustomerChange(index, 'partner_id', partner?.id || null)}
                        placeholder={t('customers.form.enterReferredBy')}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => handleRemoveAssociatedCustomer(index)}
                      className="text-sm px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    >
                      {t('customers.form.removeCustomer')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex items-center justify-end space-x-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900"
            >
              {t('customers.form.cancel')}
            </button>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting 
              ? t('customers.form.saving') 
              : isEditing 
                ? t('customers.form.updateCustomer') 
                : (associatedCustomers.length > 0 && associatedCustomers.some(c => c.first_name.trim() && c.last_name.trim() && c.email.trim()))
                  ? t('customers.form.createCustomers')
                  : t('customers.form.createCustomer')
            }
          </button>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div className="rounded-md bg-green-50 dark:bg-green-900/20 p-4 border border-green-200 dark:border-green-800">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {isEditing ? t('customers.form.customerUpdatedSuccess') : t('customers.form.customerCreatedSuccess')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {submitError && (
          <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800 dark:text-red-200">
                  {submitError}
                </p>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  )
}
